/* eslint @ngrx/prefer-effect-callback-in-block-statement: off */
import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Intercom, onHide, onShow, startConversation } from '@intercom/messenger-js-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import {
  EMPTY,
  Observable,
  catchError,
  combineLatest,
  exhaustMap,
  filter,
  fromEvent,
  fromEventPattern,
  ignoreElements,
  map,
  merge,
  of,
  race,
  startWith,
  switchMap,
  take,
  tap,
  timer,
} from 'rxjs';

import { environment } from '@ninety/ui/web/environments';

import { selectHasTier2Data } from '../../app-init/app-init.selectors';
import { appActions } from '../../app.actions';
import { INTERCOM_DEFER_MS } from '../third-party-scripts/third-party-defer';

import { FinApiService } from './fin-api.service';
import {
  AssessmentIntercomActions,
  BecomeAPartnerIntercomActions,
  DetailViewIntercomActions,
  IntercomFeedbackActions,
  IntercomLoaderActions,
  IntercomMessengerActions,
  MainLayoutIntercomActions,
  MainSidebarIntercomActions,
  NavMenuIntercomActions,
  ReportViewerIntercomActions,
} from './intercom-state.actions';
import {
  IntercomRecord,
  selectFeedbackOutcome,
  selectFinJwtValidation,
  selectIntercomMessengerOpen,
} from './intercom-state.selectors';

/** Must match the Fin workflow trigger exactly. */
export const FEEDBACK_MESSAGE = 'I would like to submit feedback';
export const PROVIDE_FEEDBACK_TIMEOUT_MS = 10_000;
/** Script tag id the Intercom SDK injects. Internal to the SDK; checked against @intercom/messenger-js-sdk 0.0.20. */
export const INTERCOM_LOADER_SCRIPT_ID = '_intercom_npm_loader';

@Injectable()
export class IntercomStateEffects {
  private actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly document = inject(DOCUMENT);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly window: any = window;

  private readonly finApiService = inject(FinApiService);

  /**
   * Defer Intercom initialization until after critical app data loads.
   *
   * Uses race(selectHasTier2Data, timer(INTERCOM_DEFER_MS)):
   * - On authenticated routes, Tier2 typically takes 3-4s so the
   *   timer wins — still moves Intercom past the early critical path.
   * - On login, Tier2 never loads so the timer is the effective trigger.
   *
   */
  initializeIntercom$ = createEffect(() =>
    this.actions$.pipe(
      ofType(appActions.bootstrap),
      exhaustMap(() =>
        race(this.store.select(selectHasTier2Data).pipe(filter(Boolean), take(1)), timer(INTERCOM_DEFER_MS))
      ),
      tap(() => Intercom({ app_id: environment.INTERCOM_APP_ID })),
      map(() => IntercomLoaderActions.booted())
    )
  );

  /** Reports the loader script's load or error, for Provide Feedback. */
  trackLoaderScript$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntercomLoaderActions.booted),
      switchMap(() => this.intercomLoaderScript$()),
      switchMap(script =>
        race(
          fromEvent(script, 'load').pipe(map(() => IntercomLoaderActions.loaded())),
          fromEvent(script, 'error').pipe(map(() => IntercomLoaderActions.failed()))
        )
      )
    )
  );

  trackMessengerVisibility$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntercomLoaderActions.booted),
      switchMap(() =>
        merge(
          fromEventPattern(handler => onShow(handler)).pipe(map(() => IntercomMessengerActions.shown())),
          fromEventPattern(handler => onHide(handler)).pipe(map(() => IntercomMessengerActions.hidden()))
        )
      )
    )
  );

  /**
   * Once Tier2 data is available the user is authenticated and their session is
   * established. Fetch the Fin JWT and pass it to Intercom so Fin can verify
   * the user's identity. Tier2 never loads on unauthenticated routes so this
   * effect is naturally a no-op on the login page.
   */
  updateIntercomWithFinJwt$ = createEffect(() =>
    // Flag is watched live so a mid-session flag flip still fetches the JWT
    combineLatest([this.store.select(selectHasTier2Data), this.store.select(selectFinJwtValidation)]).pipe(
      filter(([hasTier2, flagEnabled]) => hasTier2 && !!flagEnabled),
      take(1),
      switchMap(() =>
        this.finApiService.getFinToken().pipe(
          tap(({ access_token }: { access_token: string }) =>
            this._intercom('update', { intercom_user_jwt: access_token })
          ),
          map(() => IntercomLoaderActions.finJwtApplied()),
          catchError(() => EMPTY)
        )
      )
    )
  );

  /**
   * Ignores clicks while the Messenger is open, however it was opened.
   * Holds one click (exhaustMap ignores the rest) until Intercom is ready, then emits Ready.
   * Fails on script error or after PROVIDE_FEEDBACK_TIMEOUT_MS.
   * After Ready, fails if the Messenger doesn't open within PROVIDE_FEEDBACK_TIMEOUT_MS.
   */
  provideFeedback$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MainSidebarIntercomActions.provideFeedbackClicked),
      concatLatestFrom(() => this.store.select(selectIntercomMessengerOpen)),
      filter(([, open]) => !open),
      exhaustMap(() =>
        race(
          this.store.select(selectFeedbackOutcome).pipe(filter(Boolean)),
          timer(PROVIDE_FEEDBACK_TIMEOUT_MS).pipe(map(() => 'failed' as const))
        ).pipe(
          take(1),
          switchMap(outcome =>
            outcome === 'ready'
              ? this.failIfMessengerNeverOpens$().pipe(startWith(IntercomFeedbackActions.ready()))
              : of(IntercomFeedbackActions.failed())
          )
        )
      )
    )
  );

  startFeedbackConversation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(IntercomFeedbackActions.ready),
        tap(() => startConversation(FEEDBACK_MESSAGE))
      ),
    { dispatch: false }
  );

  showIntercomLauncher$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          AssessmentIntercomActions.showLauncher,
          ReportViewerIntercomActions.showIntercom,
          DetailViewIntercomActions.showIntercom,
          MainLayoutIntercomActions.showIntercom
        ),
        tap(_ => this._intercom('update', { hide_default_launcher: false }))
      ),
    { dispatch: false }
  );

  hideIntercomLauncher$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          AssessmentIntercomActions.hideLauncher,
          ReportViewerIntercomActions.hideIntercom,
          DetailViewIntercomActions.hideIntercom,
          MainLayoutIntercomActions.hideIntercom
        ),
        tap(_ => this._intercom('update', { hide_default_launcher: true }))
      ),
    { dispatch: false }
  );

  showIntercomMessenger$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(NavMenuIntercomActions.showMessenger, BecomeAPartnerIntercomActions.showMessenger),
        tap(_ => this._intercom('show'))
      ),
    { dispatch: false }
  );

  /** Completes once the Messenger is open; emits Failed if it never opens (e.g. widget blocked after the loader). */
  private failIfMessengerNeverOpens$(): Observable<Action> {
    return race(
      this.store.select(selectIntercomMessengerOpen).pipe(filter(Boolean), take(1), ignoreElements()),
      timer(PROVIDE_FEEDBACK_TIMEOUT_MS).pipe(map(() => IntercomFeedbackActions.failed()))
    );
  }

  /** The SDK may inject the script after Intercom() returns (if the document isn't ready yet). */
  private intercomLoaderScript$(): Observable<HTMLScriptElement> {
    return new Observable<HTMLScriptElement>(subscriber => {
      const find = () => this.document.getElementById(INTERCOM_LOADER_SCRIPT_ID) as HTMLScriptElement | null;
      const emit = (script: HTMLScriptElement) => {
        subscriber.next(script);
        subscriber.complete();
      };

      const existing = find();
      if (existing) return emit(existing);

      const observer = new MutationObserver(() => {
        const script = find();
        if (script) {
          observer.disconnect();
          emit(script);
        }
      });
      observer.observe(this.document, { childList: true, subtree: true });
      return () => observer.disconnect();
    });
  }

  /** More Info: https://developers.intercom.com/installing-intercom/web/attributes-objects/ */
  private _intercom(action: string, config?: IntercomRecord) {
    // Sometimes window.Intercom doesn't exist yet or at all.
    if (!this.window?.Intercom) return;
    else if (!config) this.window.Intercom(action);
    else this.window.Intercom(action, config);
  }
}
